import { apiClient } from '~/api/client';
import { endpoints } from '~/api/endpoints';
import type { AuthUser, AvailableTenant } from '~/store/auth.store';
import { useAuthStore } from '~/store/auth.store';

// ---------------------------------------------------------------------------
// GET /auth/me — actual response shape from the identity service
// ---------------------------------------------------------------------------

/** Casbin policy tuple as serialised by identity-service `getMe`:
 *  `[subject, domain, object, action]` → `{subject, domain, object, action}`.
 *  `object` is the resource code (e.g. `users`), `action` e.g. `read`. */
interface MeCasbinPolicy {
  subject: string;
  domain: string;
  object: string;
  action: string;
}

interface MeUserResponse {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  avatar: string | null;
  isActive: boolean;
  emailVerified: boolean;
  isSuperAdmin: boolean;
  casbin: {
    roles: string[];
    policies: MeCasbinPolicy[];
  };
}

/**
 * Real /auth/me subsidiary shape returned by identity-service. Earlier
 * iterations expected a richer payload (`type`, `depth`, `accessType` …);
 * the production API only returns the fields below, so the mapper synthesises
 * the rest with sensible defaults.
 */
interface MeSubsidiaryResponse {
  subsidiaryId: string;
  subsidiaryName: string;
  subsidiarySlug: string;
  roleCode: string;
  roleName: string;
  isSystem: boolean;
  organizationId: string;
  organizationName: string | null;
}

interface MeResponse {
  user: MeUserResponse;
  currentSubsidiary: MeSubsidiaryResponse | null;
  availableSubsidiaries: MeSubsidiaryResponse[];
  /** Resolved per-user feature-flag map. Optional because older identity-service
   *  builds didn't include it. When present, we seed the auth store immediately
   *  so downstream gates (sidebar, route guards) don't have to wait for the
   *  separate /feature-flags/me round-trip via useFeatureFlagsSync. */
  featureFlags?: Record<string, boolean> | null;
  flagsGeneration?: string | null;
}

// ---------------------------------------------------------------------------
// POST /auth/exchange-code — response shape
// ---------------------------------------------------------------------------

interface ExchangeCodeResponse {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  user: {
    id: string;
    email: string;
    firstName: string | null;
    lastName: string | null;
    avatar: string | null;
    isActive: boolean;
    emailVerified: boolean;
  };
  subsidiary: { id: string; name: string; slug: string } | null;
  role: { id: string; name: string; code: string } | null;
  availableSubsidiaries: {
    id: string;
    name: string;
    slug: string;
    role: string;
    isDefault: boolean;
    isSystem: boolean;
    type: 'ORGANIZATION' | 'SUBSIDIARY';
    depth: number;
    parentId: string | null;
    rootSubsidiaryId: string;
    organizationName: string | null;
    accessType: string;
  }[];
}

// ---------------------------------------------------------------------------
// Mappers
// ---------------------------------------------------------------------------

/**
 * Maps the identity service's /auth/me response to the frontend AuthUser type.
 * Exported for unit tests — production callers go through fetchCurrentUser.
 */
export function mapMeResponseToAuthUser(me: MeResponse): AuthUser {
  const { user, currentSubsidiary, availableSubsidiaries, featureFlags, flagsGeneration } = me;

  const firstName = user.firstName ?? '';
  const lastName = user.lastName ?? '';
  const displayName = [firstName, lastName].filter(Boolean).join(' ') || user.email;

  return {
    id: user.id,
    email: user.email,
    displayName,
    activeTenantId: currentSubsidiary?.subsidiaryId ?? '',
    // The /auth/me payload only returns subsidiaries (organizations are not
    // listed). Synthesise the AvailableTenant fields the API does not provide
    // so consumers (tenant switcher, subsidiary admin views) can render
    // without extra fetches. NOTE: `rootTenantId` mirrors `organizationId`
    // because subsidiary-table.tsx and create-subsidiary-dialog.tsx fall back
    // to it as the organization id when computing the active org scope.
    accessibleTenants: availableSubsidiaries.map((t) => ({
      id: t.subsidiaryId,
      name: t.subsidiaryName,
      slug: t.subsidiarySlug,
      roleCode: t.roleCode,
      type: 'SUBSIDIARY' as const,
      isSystem: t.isSystem,
      depth: 1,
      parentId: t.organizationId,
      rootTenantId: t.organizationId,
      organizationName: t.organizationName,
      accessType: 'DIRECT',
    })),
    roleCode: currentSubsidiary?.roleCode ?? '',
    roles: currentSubsidiary?.roleCode ? [currentSubsidiary.roleCode] : [],
    // Flatten casbin policy tuples to the `resource:action` strings that
    // requirePermission / usePermission match against (e.g. `users:read`).
    permissions: user.casbin.policies
      .filter((p) => p.object && p.action)
      .map((p) => `${p.object}:${p.action}`),
    isSuperAdmin: user.isSuperAdmin,
    avatarUrl: user.avatar ?? undefined,
    idpProvider: 'google',
    // Seed flags from /auth/me when present; useFeatureFlagsSync still runs and
    // overwrites this with the freshest data after admin toggles.
    featureFlags: featureFlags ?? {},
    ...(flagsGeneration ? { flagsGeneration } : {}),
  };
}

/**
 * Maps available subsidiaries from the exchange-code response to AvailableTenant[].
 */
function mapAvailableSubsidiaries(
  subsidiaries: ExchangeCodeResponse['availableSubsidiaries'],
): AvailableTenant[] {
  return subsidiaries.map((t) => ({
    id: t.id,
    name: t.name,
    slug: t.slug,
    roleCode: t.role,
    type: t.type,
    isSystem: t.isSystem,
    depth: t.depth,
    parentId: t.parentId,
    rootTenantId: t.rootSubsidiaryId,
    organizationName: t.organizationName,
    accessType: t.accessType,
  }));
}

// ---------------------------------------------------------------------------
// Auth API calls
// ---------------------------------------------------------------------------

/** Response shape shared by POST /auth/login and POST /auth/exchange-code. */
type AuthResponse = ExchangeCodeResponse;

/**
 * Log in with email and password.
 * Stores tokens from the response body in localStorage via auth.store.
 */
export async function loginWithCredentials(email: string, password: string): Promise<AuthResponse> {
  const response = await apiClient
    .post(endpoints.auth.login(), { json: { email, password } })
    .json<AuthResponse>();
  const store = useAuthStore.getState();
  store.setTokens(response.accessToken, response.refreshToken, 'local');
  // Record session origin so UI (e.g. the SSO banner on the password screen)
  // can branch on auth method while backend still omits `authMethod` from
  // /auth/me. See docs/auth-login-method.md.
  store.setLoginMethod('password');
  return response;
}

/**
 * Exchange an OAuth authorization code for session tokens.
 * Stores tokens from the response body in localStorage via auth.store.
 *
 * De-duplicates by `code`, keyed to survive the whole page session. An OAuth
 * code is single-use, so ANY repeat call for the same code — a concurrent
 * in-flight request, a re-render, StrictMode's double-fired effect, a remount —
 * must return the first result rather than re-POST and get a 401. The cached
 * promise is therefore cleared only on failure (so a transient network error
 * can be retried with the same code), never on success.
 */
const pendingExchanges = new Map<string, Promise<ExchangeCodeResponse>>();

export async function exchangeOAuthCode(code: string): Promise<ExchangeCodeResponse> {
  const existing = pendingExchanges.get(code);
  if (existing) return existing;

  const promise = apiClient
    .post(endpoints.auth.exchangeCode(), { json: { code } })
    .json<ExchangeCodeResponse>()
    .then((response) => {
      const store = useAuthStore.getState();
      store.setTokens(response.accessToken, response.refreshToken, 'local');
      // Record session origin (SSO) — counterpart to the 'password' write in
      // loginWithCredentials. See docs/auth-login-method.md.
      store.setLoginMethod('sso');
      return response;
    });

  // Keep the entry on success (the code is spent — a second exchange would 401);
  // drop it only on failure so a genuine retry can re-attempt the same code.
  promise.catch(() => pendingExchanges.delete(code));

  pendingExchanges.set(code, promise);
  return promise;
}

/**
 * Fetch the current authenticated user and map to the frontend AuthUser type.
 * Returns null if not authenticated (401).
 */
export async function fetchCurrentUser(): Promise<AuthUser | null> {
  try {
    const me = await apiClient.get(endpoints.auth.me()).json<MeResponse>();
    return mapMeResponseToAuthUser(me);
  } catch {
    return null;
  }
}

/**
 * Hydrate AuthUser from an auth response (login or exchange-code) + /auth/me.
 * `/auth/me`'s `currentSubsidiary` is the authoritative active subsidiary —
 * the token claim drives it server-side, so we trust whatever it returns.
 * Falls back to exchange-code data for availableSubsidiaries if /auth/me has none.
 */
async function hydrateUserFromAuthResponse(
  authResponse: AuthResponse,
  signal?: AbortSignal,
): Promise<AuthUser> {
  const me = await apiClient
    .get(endpoints.auth.me(), { signal: signal ?? null })
    .json<MeResponse>();
  const user = mapMeResponseToAuthUser(me);

  // If /auth/me returned available subsidiaries, use those; otherwise fall back to exchange-code data
  if (user.accessibleTenants.length > 0) return user;
  return {
    ...user,
    accessibleTenants: mapAvailableSubsidiaries(authResponse.availableSubsidiaries),
  };
}

/**
 * Complete the OAuth flow: exchange code for tokens, then hydrate the user.
 */
export async function fetchUserAfterOAuth(code: string, signal?: AbortSignal): Promise<AuthUser> {
  const response = await exchangeOAuthCode(code);
  return hydrateUserFromAuthResponse(response, signal);
}

/**
 * Complete the email/password flow: log in, then hydrate the user.
 */
export async function fetchUserAfterLogin(email: string, password: string): Promise<AuthUser> {
  const response = await loginWithCredentials(email, password);
  return hydrateUserFromAuthResponse(response);
}

/**
 * Verify an email address using the user_id + token from the verification link.
 * Throws on failure so callers can distinguish success from error.
 */
export async function verifyEmail(userId: string, token: string): Promise<void> {
  await apiClient.post(endpoints.auth.verifyEmail(), { json: { userId, token } });
}

/** Payload for the token-based set-password flow reached after email verification. */
export interface SetPasswordPayload {
  userId: string;
  token: string;
  newPassword: string;
}

/**
 * Set a new password using the user_id + token from the verification link.
 * This single call both verifies the email and sets the password, so the
 * one-time token is consumed exactly once. Throws on failure (e.g. expired/
 * used link) so callers can surface an error.
 */
export async function setPassword(payload: SetPasswordPayload): Promise<void> {
  await apiClient.post(endpoints.auth.setPassword(), { json: payload });
}

/** Status of an invite/verification link, used to gate the set-password page. */
export interface InviteStatus {
  valid: boolean;
  alreadySetUp: boolean;
}

/**
 * Non-consuming check of the invite link so the set-password page can decide
 * whether to render the form, redirect an already-set-up user to sign-in, or
 * show an invalid-link message — without burning the one-time token.
 */
export async function getInviteStatus(userId: string, token: string): Promise<InviteStatus> {
  return apiClient
    .get(endpoints.auth.inviteStatus(), { searchParams: { userId, token } })
    .json<InviteStatus>();
}

/**
 * Log out the current session.
 * Clears stored tokens and notifies the backend to revoke the session.
 */
export async function logout(): Promise<void> {
  await apiClient.post(endpoints.auth.logout());
  useAuthStore.getState().clearTokens();
}

/** Response shape for POST /auth/switch-subsidiary. */
interface SwitchSubsidiaryResponse {
  accessToken: string;
  refreshToken: string;
}

/**
 * POSTs `/auth/switch-subsidiary` and persists the returned tokens so any
 * subsequent request is scoped to the target subsidiary. `useTenantSwitch`
 * hard-reloads after calling this so the bootstrap re-fetches `/auth/me`.
 */
export async function switchSubsidiary(subsidiaryId: string): Promise<SwitchSubsidiaryResponse> {
  const response = await apiClient
    .post(endpoints.auth.switchSubsidiary(), { json: { subsidiaryId } })
    .json<SwitchSubsidiaryResponse>();
  useAuthStore.getState().setTokens(response.accessToken, response.refreshToken, 'session');
  return response;
}
